/**
 * Utility to POST /api/v1/filterconf/upsert with validations
 * Uses axios for requests (the project already uses axios)
 */
import axios from 'axios';


function isISODateValid(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return false;
  const d = new Date(dateStr);
  return !Number.isNaN(d.getTime());
}

function toISODateString(dateStr) {
  const d = new Date(dateStr);
  return d.toISOString();
}

function normalizeArray(val) {
  if (!val) return [];
  return Array.isArray(val) ? val : [val];
}

function findIdKeyForItem(item, preferredKeys) {
  for (const k of preferredKeys) if (k in item) return k;
  // fallback: any 'id' in key
  const idKey = Object.keys(item).find((k) => /id/i.test(k));
  return idKey || null;
}

function coerceNumericFields(item, keys) {
  for (const k of keys) {
    if (k in item && typeof item[k] === 'string' && item[k].trim() !== '') {
      const n = Number(item[k]);
      if (!Number.isNaN(n)) item[k] = n;
    }
  }
}

function dedupeByIdAndDate(arr, preferredIdKeys, dateKey = 'effective_from') {
  const seen = new Map();
  for (const item of arr) {
    const idKey = findIdKeyForItem(item, preferredIdKeys) || '';
    const id = String(item[idKey] ?? '');
    const dateVal = String(item[dateKey] ?? '');
    const key = `${id}__${dateVal}`;
    if (!seen.has(key)) seen.set(key, item);
  }
  return Array.from(seen.values());
}

function validateComponentEntries(arr, preferredIdKeys) {
  if (!Array.isArray(arr)) return { ok: false, message: 'Component should be an array' };
  for (const [i, item] of arr.entries()) {
    if (!item) return { ok: false, message: `Component entry at index ${i} is falsy` };
    const idKey = findIdKeyForItem(item, preferredIdKeys);
    if (!idKey) return { ok: false, message: `Could not find an id key in item at index ${i}` };
    coerceNumericFields(item, ['base_life_days', 'flow_drop_limit_frac', 'p2_increase_limit_frac', 'rejection_drop_limit_pp']);
    if (typeof item[idKey] !== 'string' || item[idKey].trim() === '') {
      return { ok: false, message: `${idKey} is required and must be a non-empty string (index ${i})` };
    }
    if (typeof item['base_life_days'] !== 'number' || Number.isNaN(item['base_life_days'])) {
      return { ok: false, message: `base_life_days must be a number (index ${i})` };
    }
    if (!item['effective_from'] || !isISODateValid(item['effective_from'])) {
      return { ok: false, message: `effective_from must be a valid date string (index ${i})` };
    }
  }
  return { ok: true, value: arr };
}

async function axiosPostWithRetry(pathOrUrl, body, token, attempts = 2, initialDelayMs = 500, baseUrl) {
  let attempt = 0;
  let delay = initialDelayMs;
  let lastErr = null;
  while (attempt < attempts) {
    try {
      const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
      // If a full URL is provided, use it directly
      try {
        const isAbsolute = /^https?:\/\//i.test(pathOrUrl);
        if (isAbsolute) {
          return await axios.post(pathOrUrl, body, { headers });
        }
      } catch (err) {
        if (attempt + 1 >= attempts) throw err;
      }

      // Use the base URL with the path
      if (baseUrl) {
        const u = new URL(pathOrUrl, baseUrl).toString();
        return await axios.post(u, body, { headers });
      }
      // fallback: treat as raw path (same-origin request)
      return await axios.post(pathOrUrl, body, { headers });
    } catch (err) {
      lastErr = err;
      const status = err?.response?.status;
      // If 500, allow retry after backoff
      if ((status === 500 || !status) && attempt + 1 < attempts) {
        const currentDelay = delay;
        delay *= 2;
        await new Promise((r) => setTimeout(r, currentDelay));
        attempt++;
        continue;
      }
      throw err;
    }
  }
  throw lastErr || new Error('Unknown error');
}

export async function upsertFilterConfs(payload, token, options = {}) {
  // Options accepts apiBase url
  const apiBase = options.apiBase || process.env.REACT_APP_API_URL;

  if (!payload || typeof payload !== 'object') return { ok: false, status: 400, message: 'payload must be an object' };
  const { product_id } = payload;
  if (!product_id || typeof product_id !== 'string' || product_id.trim() === '') {
    return { ok: false, status: 400, message: 'product_id is required and must be a non-empty string' };
  }

  // Prepare component lists
  const roList = normalizeArray(payload.ro);
  const sedList = normalizeArray(payload.sed);
  const carbList = normalizeArray(payload.carb);
  const alkList = normalizeArray(payload.alk);

  const roValid = validateComponentEntries(roList, ['ro_mem_id', 'ro_id', 'ro']);
  if (!roValid.ok) return { ok: false, status: 400, message: roValid.message };
  const sedValid = validateComponentEntries(sedList, ['sed_id', 'sediment_id', 'sed']);
  if (!sedValid.ok) return { ok: false, status: 400, message: sedValid.message };
  const carbValid = validateComponentEntries(carbList, ['carb_id', 'carbon_id', 'carbon']);
  if (!carbValid.ok) return { ok: false, status: 400, message: carbValid.message };
  const alkValid = validateComponentEntries(alkList, ['alk_id', 'alkaline_id', 'alk']);
  if (!alkValid.ok) return { ok: false, status: 400, message: alkValid.message };

  // Dedupe
  const roUnique = dedupeByIdAndDate(roList, ['ro_mem_id', 'ro_id', 'ro']);
  const sedUnique = dedupeByIdAndDate(sedList, ['sed_id', 'sediment_id', 'sed']);
  const carbUnique = dedupeByIdAndDate(carbList, ['carb_id', 'carbon_id', 'carbon']);
  const alkUnique = dedupeByIdAndDate(alkList, ['alk_id', 'alkaline_id', 'alk']);

  // Normalize effective_from to ISO strings
  const normalizeDates = (arr) => arr.map((it) => ({ ...it, effective_from: toISODateString(it.effective_from) }));
  const body = {
    product_id,
    ...(roUnique.length ? { ro: normalizeDates(roUnique) } : {}),
    ...(sedUnique.length ? { sed: normalizeDates(sedUnique) } : {}),
    ...(carbUnique.length ? { carb: normalizeDates(carbUnique) } : {}),
    ...(alkUnique.length ? { alk: normalizeDates(alkUnique) } : {}),
  };

  try {
    // Always target /api/v1/filterconf/upsert by default
    const upsertPath = options.upsertPath || '/api/v1/filterconf/upsert';
    const res = await axiosPostWithRetry(upsertPath, body, token, 2, 500, apiBase);
    // axios returns a promise that resolves with response object or throws
    if (res && res.status === 200) {
      return { ok: true, status: 200, data: res.data };
    }
    // handle known status codes
    const status = res?.status || 500;
    if ([400, 403, 409].includes(status)) {
      const message = (res?.data?.message) || (res?.data) || 'Bad request';
      return { ok: false, status, message };
    }
    return { ok: false, status, message: `Unexpected response ${status}` };
  } catch (err) {
    // If axios threw, err.response may exist
    const status = err?.response?.status || 0;
    if ([400, 403, 409].includes(status)) {
      const msg = err?.response?.data?.message || err?.message || 'API error';
      return { ok: false, status, message: msg };
    }
    return { ok: false, status, message: err?.message || 'Unknown network error' };
  }
}

export default upsertFilterConfs;
